import React, { useContext, useEffect, useState } from 'react'
import { of } from 'rxjs'
import { DraggableLayout, IDraggableZone, Tag, getDragResultDelta, makeStyles, updateAfterDragResult, useObservable } from '@cicero/ui'
import { AttributeOptionDoc, AttributeSchema, AttributeValueSchema, PageDoc, PageSchema, StoreContext, useModel } from 'models'
import { IPageFilter } from 'models/filters'
import { PageCard } from 'views'
import { AttributeOption, PageQuickCreateForm } from 'forms'
import { EditorContext, IDashboardWidgetElement, IEditorWidget } from '../EditorService'
import { useAttributesInOrder } from 'models/hooks'

export const Kanban = ({
  config: {
    display_attribute_ids,
    empty_group_name,
    filter_template_ids,
    filters = [],
    group_by_attribute_id,
  },
}: {
  config: IKanbanWidgetConifg
}) => {
  const { kanbanCell, kanbanCellLoading, kanbanPlacementCell, kanbanHeader } = useStyles()
  const store = useContext(StoreContext)
  const editor = useContext(EditorContext)
  const Attributes = useModel(AttributeSchema)
  const AttributeValues = useModel(AttributeValueSchema)
  const [zones, setZones] = useState<IKanbanZone[][]>([])
  const [isLoading, results] = PageSchema.HOOKS.usePageFilters(filters)
  const attribute = useObservable(() => group_by_attribute_id ? Attributes.GET$(group_by_attribute_id) : of(null), null, [group_by_attribute_id])
  const groups = PageSchema.HOOKS.usePagesGroupedByAttribute(results, attribute)
  const options = AttributeSchema.HOOKS.useAttributeOptions(attribute)
  const displayAttributes = useAttributesInOrder(display_attribute_ids)

  useEffect(() => {
    const z: IKanbanZone[] = [
      { id: '_blank', title: empty_group_name || 'No Value', option: null, cards: groups['_blank'] || [] },
      ...options.map(o => ({ id: o.id, title: o.value, option: o as any, cards: groups[o.id] || [] })),
    ]

    setZones([z])
    // LOGGER.debug('got results', { attribute: attribute?.label, results, groups, options, z })
  }, [results, groups, options])

  return  <>
    <DraggableLayout
      zones={zones}
      classes={{
        container: isLoading ? kanbanCellLoading : undefined,
        cell: kanbanCell,
        placementList: kanbanPlacementCell,
      }}
      renderPlacementHeader={(zone) => {
        return <span className={kanbanHeader}>
          { zone.option ? <AttributeOption option={zone.option} /> : <Tag title={zone.title || ''} /> }
        </span>
      }}
      renderPlacementFooter={(zone) => <PageQuickCreateForm
        buttonText="+ Add"
        template_id={filter_template_ids ? filter_template_ids[0] : undefined}
        parent_id={editor.page ? editor.page.id : undefined}
        afterCreate={(page) => {
          if (!attribute || zone.id === 'blank') { return }
          AttributeSchema.HOOKS.persistAttributeValueChanges(store, page, attribute, [], [zone.id])
        }}
      />}
      renderCard={(page: PageDoc) => <PageCard page={page} attributes={displayAttributes} quickOpen />}
      onDragEnd={async (r) => {
        const { sourceAddress, targetAddress, sourceZone, sourceCard, targetZone } = getDragResultDelta<IKanbanZone>(r, zones)
        if (sourceAddress && targetAddress && sourceCard && sourceZone && targetZone && group_by_attribute_id && attribute) {
          const { option } = targetZone
          setZones(updateAfterDragResult(zones, { card: sourceCard, sourceAddress, targetAddress }))
          const currentValues = await AttributeValues.QUERY({ selector: { page_id: sourceCard?.id, attribute_id: group_by_attribute_id } })
          await AttributeSchema.HOOKS.persistAttributeValueChanges(store, sourceCard, attribute, currentValues, option ? [option] : [])
        }
      }}
    />
  </>
}

const useStyles = makeStyles({
  '@keyframes loading': {
    '0%': { background: '#FFF' },
    '50%': { background: '#EEE' },
    '100%': { background: '#FFF' },
  },
  kanbanHeader: {
    display: 'block',
    padding: 16,
  },
  kanbanCellLoading: {
    // border: 0,
    // animation: '$loading 3s infinite',
  },
  kanbanCell: {
    border: 0,
  },
  kanbanPlacementCell: {
    // minHeight: 250,
  },
})

export const kanbanWidget: IEditorWidget = {
  id: 'kanban',
  name: 'Kanban Board',
  description: 'Show pages in columns based on a property',
  image: require('./img/kanban.png'),
  renderer: (leaf) => <Kanban config={leaf} />,
}

export interface IKanbanZone extends IDraggableZone {
  option: AttributeOptionDoc|null
  cards?: PageDoc[]
}

export interface IKanbanWidgetConifg extends IDashboardWidgetElement {
  display_attribute_ids?: string[]
  empty_group_name?: string
  filter_template_ids?: string[]
  filters?: IPageFilter[]
  group_by_attribute_id?: string
}
